import { useState } from "react";
import { Image as ImageIcon, Loader2, Undo2, Search, Copy, Rocket } from "lucide-react";
import { Button } from "./ui/button";
import api from "../lib/api";

const when = (iso) => (iso ? new Date(iso).toLocaleString() : "");

// Cart line thumbnails: dry-run, apply or revert the Liquid patch on the live theme's cart templates.
export const ShopThemePatch = ({ theme, onChanged }) => {
  const [plan, setPlan] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");

  const run = async (label, fn) => {
    setBusy(label);
    setError("");
    try {
      await fn();
    } catch (e) {
      setError(e?.response?.data?.detail || "Shopify request failed");
    }
    setBusy("");
  };
  const check = () => run("check", async () => setPlan((await api.get("/library/shop/theme")).data));
  const apply = () => run("apply", async () => { await api.post("/library/shop/theme/apply"); setPlan(null); await onChanged(); });
  const revert = () => run("revert", async () => { await api.post("/library/shop/theme/revert"); setPlan(null); await onChanged(); });
  const draftPatch = () => run("draft", async () => setDraft((await api.post("/library/shop/theme/draft")).data));
  const publishDraft = () => run("publish", async () => { await api.post("/library/shop/theme/publish"); setDraft(null); await onChanged(); });
  const spots = (plan?.files || []).reduce((n, f) => n + f.spots, 0) + (plan?.layout_pending ? 1 : 0);

  return (
    <div data-testid="shop-theme-panel" className="rounded-2xl border border-border bg-background/60 p-4 space-y-3">
      <p className="flex items-center gap-2 text-xs font-mono2 uppercase tracking-widest text-muted-foreground"><ImageIcon className="h-4 w-4" /> Cart thumbnails + product page</p>
      <p className="text-xs text-muted-foreground">Patches the live theme so each Duckify cart line shows that duck's own painted picture, and — when a customer opens the Custom Duck product from their cart — the product page shows that duck's turntable (front hero + side thumbnails) and description. Also adds the basket cart sync, so a maker's whole basket lands in their cart in one tap with no duplicates. Backed up; revertible.</p>
      {theme && (
        <p data-testid="shop-theme-cart-sync" className={`text-xs ${theme.cart_sync ? "text-foreground" : "text-destructive"}`}>
          Basket cart sync: {theme.cart_sync ? "installed" : "not installed yet. Check theme, then Apply patch"}
        </p>
      )}
      {theme ? (
        <p data-testid="shop-theme-status" className="text-xs text-foreground">
          Patched <span className="font-semibold">{theme.theme_name}</span> · {when(theme.at)} · {(theme.files || []).map((f) => `${f.filename} (${f.spots})`).join(", ")}
        </p>
      ) : (
        <p data-testid="shop-theme-status" className="text-xs text-muted-foreground">Theme not patched yet.</p>
      )}
      {plan && (
        <ul data-testid="shop-theme-plan" className="text-xs font-mono2 space-y-1">
          <li className="text-muted-foreground">Live theme: {plan.theme.name}</li>
          {plan.files.length === 0 && <li className="text-destructive">No cart template files matched — the theme may use unusual file names.</li>}
          {plan.files.map((f) => (
            <li key={f.filename}>{f.filename} · {f.patched ? "already patched" : `${f.spots} image spot${f.spots === 1 ? "" : "s"}`}</li>
          ))}
          <li>layout/theme.liquid · basket cart sync {plan.cart_sync?.injected ? "already installed" : "to install"}</li>
        </ul>
      )}
      <div className="flex flex-wrap gap-2">
        <Button data-testid="shop-theme-check-btn" variant="outline" size="sm" className="rounded-full gap-2" onClick={check} disabled={busy !== ""}>
          {busy === "check" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Check theme
        </Button>
        <Button data-testid="shop-theme-apply-btn" size="sm" className="rounded-full gap-2" onClick={apply} disabled={busy !== "" || !plan || spots === 0}>
          {busy === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImageIcon className="h-4 w-4" />} Apply patch{spots ? ` (${spots})` : ""}
        </Button>
        <Button data-testid="shop-theme-draft-btn" variant="outline" size="sm" className="rounded-full gap-2" onClick={draftPatch} disabled={busy !== ""}>
          {busy === "draft" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Copy className="h-4 w-4" />} Patch a draft copy
        </Button>
        {theme && (
          <Button data-testid="shop-theme-revert-btn" variant="ghost" size="sm" className="rounded-full gap-2" onClick={revert} disabled={busy !== ""}>
            {busy === "revert" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Revert
          </Button>
        )}
      </div>
      {draft && (
        <p data-testid="shop-theme-draft" className="text-xs">
          Draft copy <span className="font-semibold">{draft.theme_name}</span> patched ({(draft.files || []).join(", ")}) —{" "}
          <a href={draft.preview_url} target="_blank" rel="noreferrer" className="underline">preview it</a>, then publish it when it looks right.
        </p>
      )}
      {draft && (
        <Button data-testid="shop-theme-publish-btn" size="sm" className="rounded-full gap-2" onClick={publishDraft} disabled={busy !== ""}>
          {busy === "publish" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Rocket className="h-4 w-4" />} Publish draft copy
        </Button>
      )}
      {error && <p data-testid="shop-theme-error" className="text-sm text-destructive">{error}</p>}
    </div>
  );
};
